/**
 * Runtime configuration for the mobile-mapping app.
 * This enables single build + config swap deployment strategy.
 *
 * All environment-specific values are loaded at runtime from /config.json,
 * allowing a single build artifact to be deployed to any environment.
 */

import { LoggerConfig } from '@trimble/logging';

export interface TidSettings {
  readonly appName: string;
  readonly version: string;
  readonly clientId: string;
  readonly configurationEndpoint: string;
  readonly scopes: readonly string[];
}

export interface AppConfig {
  readonly envName: string;
  readonly gainsightKey: string;
  readonly deployedTag?: string;
  readonly production: boolean;
  readonly seuratDomain: string;
  readonly alignmentsDomain: string;
  readonly mobileMappingDomain: string;
  readonly imagingDomain: string;
  readonly tcTcpsDomain: string;
  readonly tcUserDomain: string;
  readonly ecomDomain: string;
  readonly helpUrl: string;
  readonly coordinatesDomain: string;
  readonly profileUrl: string;
  readonly connectUrl: string;
  readonly mapTileServiceBaseUrl: string;
  readonly mapTileLayerQueryParams: string;
  readonly mapTileAuthToken: string;
  readonly fxWorkspaceDomain?: string;
  readonly fxMapsServiceBaseUrl?: string;
  readonly fxFeaturesServiceBaseUrl?: string;
  readonly fxTilesServiceBaseUrl?: string;
  readonly tidSettings: TidSettings;
  readonly loggers: LoggerConfig[];
}

let appConfig: AppConfig | null = null;

const REQUIRED_FIELDS: (keyof AppConfig)[] = [
  'envName',
  'gainsightKey',
  'production',
  'seuratDomain',
  'alignmentsDomain',
  'mobileMappingDomain',
  'imagingDomain',
  'tcTcpsDomain',
  'tcUserDomain',
  'ecomDomain',
  'helpUrl',
  'coordinatesDomain',
  'profileUrl',
  'connectUrl',
  'mapTileServiceBaseUrl',
  'mapTileLayerQueryParams',
  'mapTileAuthToken',
  'tidSettings',
  'loggers',
];

const REQUIRED_TID_SETTINGS_FIELDS: (keyof TidSettings)[] = [
  'appName',
  'version',
  'clientId',
  'configurationEndpoint',
  'scopes',
];

/**
 * Loads the application configuration from /config.json.
 * This should be called once at application startup before rendering.
 *
 * @returns The loaded configuration
 * @throws Error if the config cannot be loaded or parsed
 */
export async function loadConfig(): Promise<AppConfig> {
  if (appConfig !== null) {
    return appConfig;
  }

  let response: Response;
  try {
    response = await fetch(`${import.meta.env.BASE_URL}config.json`);
  } catch (error) {
    const details =
      error instanceof Error && error.message ? ` - ${error.message}` : '';
    throw new Error(
      `Failed to load config: network error or unreachable ${import.meta.env.BASE_URL}config.json${details}`
    );
  }

  if (!response.ok) {
    throw new Error(
      `Failed to load config: ${response.status} ${response.statusText}`
    );
  }

  const config = (await response.json()) as AppConfig;

  const missingFields = REQUIRED_FIELDS.filter(
    (field) => config[field] === undefined || config[field] === null
  );

  if (missingFields.length > 0) {
    throw new Error(
      `Invalid config: missing required fields (${missingFields.join(', ')})`
    );
  }

  const missingTidSettingsFields = REQUIRED_TID_SETTINGS_FIELDS.filter(
    (field) =>
      config.tidSettings[field] === undefined ||
      config.tidSettings[field] === null
  );

  if (missingTidSettingsFields.length > 0) {
    throw new Error(
      `Invalid config: missing required tidSettings fields (${missingTidSettingsFields.join(', ')})`
    );
  }

  appConfig = config;
  return appConfig;
}

/**
 * Returns the loaded application configuration.
 * Must be called after loadConfig() has completed.
 *
 * @returns The application configuration
 * @throws Error if config has not been loaded yet
 */
export function getConfig(): AppConfig {
  if (appConfig === null) {
    throw new Error(
      'Config not loaded. Call loadConfig() before accessing config.'
    );
  }

  return appConfig;
}

/**
 * Resets the config state. Only for testing purposes.
 * @internal
 */
export function _resetConfigForTesting(): void {
  appConfig = null;
}

/**
 * Sets the config directly. Only for testing purposes.
 * @internal
 */
export function _setConfigForTesting(config: AppConfig): void {
  appConfig = config;
}
